// The weekly digest (task kind learning_digest, assigned to the owner; docs/specs/LEARNING_BASE.md): the lessons Ogma
// learned from results this week, which already apply themselves. Veto any one (set_lesson_status vetoed); Looks good
// closes the task (complete_task).
import { useState } from "react";
import { Link } from "react-router-dom";
import type { Crm } from "../../lib/crm";
import type { HumanTask } from "../../lib/types";
import { CONFIDENCE_WORDS, readDigest, setLessonStatus } from "../../lib/learning";
import { fmtDate, plural } from "../../lib/format";
import { Button } from "../kit";
import { Notice } from "../ui";
import "./whats-working.css";

export function learningDigestSummary(t: HumanTask): string {
  const n = readDigest(t.payload).lessons.length;
  return n ? `${n} new ${plural(n, "lesson")} from this week's results` : "This week's lessons from results";
}

export function LearningDigestTask({ crm, task, onDone }: { crm: Crm; task: HumanTask; onDone: () => void }) {
  const d = readDigest(task.payload);
  const [vetoed, setVetoed] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const run = async (key: string, f: () => Promise<unknown>) => {
    setBusy(key);
    setErr(null);
    try {
      await f();
      return true;
    } catch (e) {
      setErr((e as Error).message);
      return false;
    } finally {
      setBusy(null);
    }
  };
  const veto = async (id: string) => {
    if (await run(id, () => setLessonStatus(crm, id, "vetoed"))) setVetoed((v) => [...v, id]);
  };
  const done = async () => {
    if (await run("done", () => crm.completeTask(task.id, "done", { vetoed }))) onDone();
  };
  return (
    <div className="whats-working">
      <p className="small">
        {d.week ? `Week of ${fmtDate(d.week)}. ` : ""}Ogma already uses these. Veto any you disagree with. <Link to="/campaigns/list/working">What's working</Link>
      </p>
      {d.lessons.length > 0 && (
        <ul className="ww-list">
          {d.lessons.map((l) => (
            <li key={l.id} className="ww-row ww-lesson">
              <span className="ww-what">
                {l.confidence && <span className="ww-kind">{CONFIDENCE_WORDS[l.confidence]}</span>}
                <span>{l.statement}</span>
              </span>
              {vetoed.includes(l.id) ? (
                <span className="muted small" role="status">Vetoed</span>
              ) : (
                <Button variant="ghost" small disabled={busy !== null} onClick={() => void veto(l.id)} aria-label={`Veto: ${l.statement}`}>Veto</Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <Notice text={err} tone="bad" />
      <div className="action-bar">
        <Button variant="primary" small disabled={busy !== null} onClick={() => void done()}>{busy === "done" ? "Saving…" : "Looks good"}</Button>
      </div>
    </div>
  );
}
